<template>
  <div class="form-grid">
    <div class="form-item full">
      <label class="form-label required">綁定分類</label>
      <el-cascader v-model="form.categoryPath" :options="tree" :props="treeProps" clearable filterable
                   separator=" / " placeholder="請選擇分類"/>
    </div>
    <div class="form-item full">
      <label class="form-label required">文章標題</label>
      <el-input v-model="form.title" placeholder="請輸入文章標題"/>
    </div>

    <div class="form-item">
      <label class="form-label">作者</label>
      <el-input v-model="form.author" placeholder="請輸入作者"/>
    </div>
    <div class="form-item">
      <label class="form-label">區域</label>
      <el-input v-model="form.region" placeholder="請輸入區域"/>
    </div>

    <div class="form-item full">
      <label class="form-label">摘要</label>
      <el-input v-model="form.summary" type="textarea" :rows="3" placeholder="請輸入文章摘要"/>
    </div>
    <div class="form-item full">
      <label class="form-label">標籤</label>
      <div class="tag-editor">
        <span v-for="(tag,index) in tagList" :key="tag+index">{{ tag }} <button type="button" @click="removeTag(index)">×</button></span>
        <input v-model.trim="tagInput" placeholder="輸入後按 Enter 新增" @keyup.enter="addTag" @blur="addTag">
      </div>
    </div>

    <upload-box label="封面圖" box-title="文章封面圖" detail="1200 × 630 px" :value="form.coverImage"
                @upload="upload($event,'coverImage')"/>
    <div class="form-item">
      <label class="form-label">多圖</label>
      <div class="gallery-upload-box">
        <div class="gallery-empty" v-if="!images.length">
          <div class="gallery-empty-icon"><i class="el-icon-picture-outline"/></div>
          <div class="gallery-empty-text">
            <strong>文章圖片集</strong>
            <small>建議尺寸：建議寬度 1200 px；支援 JPG、PNG、SVG、GIF</small>
          </div>
          <el-button size="mini" @click="$refs.images.click()">選擇圖片</el-button>
        </div>
        <div class="gallery-grid" v-else>
          <div v-for="(image,index) in images" :key="image+index" class="gallery-item">
            <div class="gallery-thumb" :style="{ backgroundImage: `url(${image})` }"></div>
            <button type="button" @click="removeImage(index)"><i class="el-icon-close"/></button>
          </div>
          <button type="button" class="gallery-add" @click="$refs.images.click()">
            <i class="el-icon-plus"/>
            <span>新增</span>
          </button>
        </div>
        <input ref="images" hidden type="file" accept="image/*" @change="pickImages">
      </div>
    </div>

    <div class="form-item">
      <label class="form-label">外鏈</label>
      <el-input v-model="form.externalUrl" placeholder="https://"/>
    </div>
    <div class="form-item">
      <label class="form-label">自訂路由</label>
      <el-input v-model="form.customUrl" placeholder="例如 /article/digital-skills"/>
    </div>

    <div class="form-item triple">
      <label class="form-label required">發布日期</label>
      <el-date-picker v-model="form.publishTime" type="datetime" value-format="yyyy-MM-dd HH:mm:ss"/>
    </div>
    <div class="form-item triple">
      <label class="form-label">排序</label>
      <el-input-number v-model="form.sort" :min="0" :controls="false"/>
    </div>
    <div class="form-item triple">
      <label class="form-label required">狀態</label>
      <el-select v-model="form.status">
        <el-option label="有效" value="0"/>
        <el-option label="關閉" value="1"/>
      </el-select>
    </div>

    <div class="form-item full">
      <label class="form-label required">內容</label>
      <quill-editor v-model="form.content" class="quill-editor" :options="editorOptions"/>
    </div>

    <div class="section-title form-item full">擴展欄位</div>
    <div class="form-item">
      <label class="form-label">備註欄位1</label>
      <el-input v-model="form.remark1"/>
    </div>
    <div class="form-item">
      <label class="form-label">備註欄位2</label>
      <el-input v-model="form.remark2"/>
    </div>

    <div class="section-title form-item full">
      <span>SEO 三要素</span>
      <small>SEO 欄位獨立管理，不與擴展欄位混用。</small>
    </div>
    <div class="form-item">
      <label class="form-label">SEO 標題</label>
      <el-input v-model="form.seoTitle"/>
    </div>
    <div class="form-item">
      <label class="form-label">SEO 關鍵字</label>
      <el-input v-model="form.seoKeywords"/>
    </div>
    <div class="form-item full">
      <label class="form-label">SEO 描述</label>
      <el-input v-model="form.seoDescription" type="textarea" :rows="3"/>
    </div>
  </div>
</template>

<script>
import {quillEditor} from 'vue-quill-editor';
import UploadBox from './UploadBox.vue';

export default {
  components: {quillEditor, UploadBox},
  props: {
    form: {type: Object, required: true},
    tree: {type: Array, default: () => []},
    images: {type: Array, default: () => []},
    upload: {type: Function, required: true},
    removeImage: {type: Function, required: true}
  },
  data() {
    return {
      treeProps: {value: 'id', label: 'name', children: 'children', checkStrictly: true, emitPath: true, expandTrigger: 'hover'},
      tagInput: '',
      editorOptions: {
        modules: {toolbar: [['bold', 'italic', 'underline'], [{list: 'ordered'}, {list: 'bullet'}], ['link', 'image']]},
        placeholder: '請輸入文章內容'
      }
    }
  },
  computed: {
    tagList() {
      return this.form.tags ? String(this.form.tags).split(',').map(v => v.trim()).filter(Boolean) : []
    }
  },
  methods: {
    addTag() {
      if (!this.tagInput) return;
      const list = this.tagList.slice();
      if (!list.includes(this.tagInput)) list.push(this.tagInput);
      this.form.tags = list.join(',');
      this.tagInput = ''
    },
    removeTag(index) {
      const list = this.tagList.slice();
      list.splice(index, 1);
      this.form.tags = list.join(',')
    },
    pickImages(e) {
      const f = e.target.files && e.target.files[0];
      if (f) this.upload(f, 'images');
      e.target.value = ''
    }
  }
}
</script>
